import { useMemo } from "react"
import {
  MapPin,
  Phone,
  Clock,
  CalendarClock,
  AlertTriangle,
} from "lucide-react"
import { Link } from "react-router-dom"
import { api } from "../../convex/_generated/api"
import { FancyBrand } from "@/components/fancy-brand"
import { LazyGoogleMap } from "@/components/lazy-google-map"
import { useConvexQuery } from "@/hooks/use-convex-query"
import { businessInfo, openingHours } from "@/lib/business-info"
import { dimensionsFromHeight, MASCOT_INTRINSIC } from "@/lib/brand-assets"
import {
  formatDateShort,
  getLocalDateKey,
  isTemporaryHoursActive,
  parseTemporaryHours,
} from "@/lib/temporary-hours"

export function Footer() {
  const temporaryHoursRaw = useConvexQuery(api.settings.get, {
    key: "temporaryHours",
  })

  const temporaryHoursActive = useMemo(() => {
    const data = parseTemporaryHours(temporaryHoursRaw)
    if (!data) return null

    return isTemporaryHoursActive(data, getLocalDateKey()) ? data : null
  }, [temporaryHoursRaw])

  return (
    <footer
      id="kontakt"
      className="relative border-t border-border/50 bg-card/30 backdrop-blur-sm"
    >
      {/* Top divider */}
      <div className="absolute top-0 right-0 left-0 h-px bg-gradient-to-r from-transparent via-zielarz-green/30 to-transparent" />

      <div className="mx-auto max-w-7xl px-6 py-16">
        {/* Google Maps */}
        <div className="mb-12">
          <div className="overflow-hidden rounded-2xl border border-border/50 shadow-lg">
            <LazyGoogleMap />
          </div>
        </div>

        <div className="grid gap-10 md:grid-cols-3">
          {/* Address */}
          <div>
            <div className="mb-4 flex items-center gap-2">
              <MapPin className="size-5 text-zielarz-green" />
              <h3 className="text-lg font-bold">Nasz Sklep</h3>
            </div>
            <address className="leading-relaxed text-muted-foreground not-italic">
              <p>{businessInfo.address.streetAddress}</p>
              <p>
                {businessInfo.address.postalCode}{" "}
                {businessInfo.address.addressLocality}
              </p>
              <p>{businessInfo.plusCode}</p>
              <p className="mt-3 flex items-center gap-2">
                <Phone className="size-4" />
                <a
                  href={businessInfo.phone.href}
                  className="transition-colors hover:text-zielarz-green"
                >
                  {businessInfo.phone.display}
                </a>
              </p>
              <p className="mt-3 flex items-center gap-2">
                <svg
                  className="size-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                </svg>
                <a
                  href={businessInfo.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-zielarz-green"
                >
                  Facebook
                </a>
              </p>
            </address>
          </div>

          {/* Hours */}
          <div>
            <div className="mb-4 flex items-center gap-2">
              {temporaryHoursActive ? (
                <CalendarClock className="size-5 text-zielarz-green" />
              ) : (
                <Clock className="size-5 text-zielarz-green" />
              )}
              <h3 className="text-lg font-bold">Godziny Otwarcia</h3>
              {temporaryHoursActive && (
                <span className="rounded-full bg-zielarz-yellow/15 px-2 py-0.5 text-xs font-medium text-zielarz-yellow">
                  tymczasowe
                </span>
              )}
            </div>

            {temporaryHoursActive ? (
              <>
                <div className="space-y-2 text-muted-foreground">
                  {temporaryHoursActive.days.map((day) => (
                    <div key={day.date} className="flex justify-between">
                      <span>
                        {day.dayLabel}{" "}
                        <span className="text-xs">
                          ({formatDateShort(day.date)})
                        </span>
                      </span>
                      {day.opens && day.closes ? (
                        <span className="font-semibold text-foreground">
                          {day.opens} - {day.closes}
                        </span>
                      ) : (
                        <span className="font-medium text-destructive">
                          Zamknięte
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Godziny obowiązują od{" "}
                  {formatDateShort(temporaryHoursActive.startDate)} do{" "}
                  {formatDateShort(temporaryHoursActive.endDate)}
                </p>
              </>
            ) : (
              <div className="space-y-2 text-muted-foreground">
                {openingHours.map((entry) => (
                  <div key={entry.dayLabelPl} className="flex justify-between">
                    <span>{entry.dayLabelPl}</span>
                    {entry.opens && entry.closes ? (
                      <span className="font-semibold text-foreground">
                        {entry.opens} - {entry.closes}
                      </span>
                    ) : (
                      <span className="font-medium text-destructive">
                        Zamknięte
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 18+ warning */}
          <div className="flex flex-col items-start md:items-end">
            <div className="flex items-center gap-3 rounded-2xl border border-destructive/30 bg-destructive/10 px-5 py-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-full border-2 border-destructive bg-destructive/20 text-lg font-extrabold text-destructive">
                18+
              </div>
              <div>
                <p className="text-sm font-bold text-destructive">
                  TYLKO DLA DOROSŁYCH.
                </p>
                <p className="text-xs text-muted-foreground">
                  Wyrób tytoniowy/vape.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-border/50 pt-8 md:flex-row">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/assets/brand/mascot.svg"
              alt="Zielarz"
              className="h-14 w-auto object-contain"
              {...dimensionsFromHeight(MASCOT_INTRINSIC, 56)}
            />
          </Link>

          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} Vape Shop <FancyBrand size="sm" />
            . Wszelkie prawa zastrzeżone.
          </p>

          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <AlertTriangle className="size-3" />
            <span>Produkt zawiera nikotynę. Nikotyna uzależnia.</span>
          </div>
        </div>
      </div>
    </footer>
  )
}
